import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

function AnimatedNumber({ value, prefix = "", suffix = "", decimals = 0 }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const target = typeof value === "number" ? value : parseFloat(value) || 0;
    const duration = 600;
    const start = 0;
    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(start + (target - start) * eased);
      if (progress < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [value]);

  const formatted = decimals > 0
    ? display.toFixed(decimals)
    : Math.round(display).toLocaleString();

  return <span>{prefix}{formatted}{suffix}</span>;
}

export default function HomepageROICalculator() {
  const [propertyValue, setPropertyValue] = useState("");
  const [images, setImages] = useState("");
  const [showResults, setShowResults] = useState(false);
  const resultsRef = useRef(null);

  const numImages = Math.min(Math.max(parseInt(images) || 0, 0), 20);
  const propVal = parseFloat(propertyValue) || 0;

  // Pricing logic
  const stagingCost = numImages * 65;

  // Scenarios
  const scenarios = [
    { percent: 1, label: "1%" },
    { percent: 3, label: "3%" },
    { percent: 5, label: "5%" },
  ].map((s) => {
    const additionalValue = Math.round(propVal * (s.percent / 100));
    const returnMultiple = stagingCost > 0 ? (additionalValue / stagingCost) : 0;
    return { ...s, additionalValue, returnMultiple };
  });

  const handleCalculate = (e) => {
    e.preventDefault();
    if (propVal > 0 && numImages > 0) {
      setShowResults(true);
      // GA event
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "calculator_completed", {
          event_category: "ROI Calculator",
          property_value: propVal,
          images: numImages,
          staging_cost: stagingCost,
        });
      }
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  };

  const handleCTAClick = () => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "calculator_cta_click", {
        event_category: "ROI Calculator",
        property_value: propVal,
        staging_cost: stagingCost,
      });
    }
  };

  return (
    <section data-testid="homepage-roi-calculator" className="py-28 lg:py-36 bg-[#FAFAF8]">
      <div className="max-w-4xl mx-auto px-6 lg:px-16">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="uppercase text-[11px] tracking-[0.3em] text-[#758C7B] mb-4 font-medium">
            Free Calculator
          </p>
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-[2.5rem] tracking-[-0.01em] text-[#0D2D54] font-extralight mb-5">
            What could an empty property be costing you?
          </h2>
          <p className="text-[15px] text-[#6B7280] leading-[1.8] max-w-xl mx-auto">
            Buyers decide in seconds. See how a small investment in virtual staging could help showcase your property's potential and make a stronger first impression online.
          </p>
        </div>

        {/* Calculator Form */}
        <form onSubmit={handleCalculate} className="max-w-md mx-auto space-y-5 mb-6">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-[#6B7280] mb-2 font-medium">
              Property Value (&pound;)
            </label>
            <input
              type="number"
              data-testid="roi-home-property-value"
              value={propertyValue}
              onChange={(e) => { setPropertyValue(e.target.value); setShowResults(false); }}
              placeholder="e.g. 300,000"
              className="w-full border border-[#E8E8E4] px-4 py-3.5 text-[15px] text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none transition-colors"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-[#6B7280] mb-2 font-medium">
              Number of Images to Stage
            </label>
            <input
              type="number"
              data-testid="roi-home-images"
              value={images}
              onChange={(e) => { setImages(e.target.value); setShowResults(false); }}
              placeholder="e.g. 6"
              min="1"
              max="20"
              className="w-full border border-[#E8E8E4] px-4 py-3.5 text-[15px] text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none transition-colors"
            />
            {numImages > 0 && (
              <p className="text-xs text-[#758C7B] mt-2">
                {numImages} image{numImages !== 1 ? "s" : ""} &times; &pound;65 = <strong className="text-[#0D2D54]">&pound;{stagingCost.toLocaleString()}</strong>
              </p>
            )}
          </div>
          <button
            type="submit"
            data-testid="roi-home-calculate-btn"
            className="w-full bg-[#0D2D54] text-white py-4 text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-[#1A3D6B] transition-all"
          >
            Show My Results
          </button>
        </form>

        {/* Results */}
        {showResults && (
          <div ref={resultsRef} className="mt-16 animate-fade-in-up" style={{ animationDuration: "0.5s" }}>
            <div className="text-center mb-10">
              <h3 className="font-['Outfit'] text-xl sm:text-2xl lg:text-[1.8rem] font-extralight text-[#0D2D54] mb-3">
                Could you be leaving money on the table?
              </h3>
              <p className="text-[14px] text-[#6B7280] max-w-lg mx-auto leading-[1.7]">
                Based on the property value you entered, here's what different uplift scenarios could look like.
              </p>
            </div>

            {/* Scenario Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6 mb-8">
              {scenarios.map((s, i) => (
                <div
                  key={i}
                  data-testid={`roi-scenario-${i}`}
                  className="bg-white border border-[#E8E8E4] p-6 text-center opacity-0 animate-fade-in-up"
                  style={{ animationDelay: `${i * 0.15}s`, animationFillMode: "forwards" }}
                >
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#758C7B] mb-4 font-medium">
                    {s.label} Uplift Scenario
                  </p>
                  <div className="mb-4">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280] mb-1">Potential Additional Value</p>
                    <p className="font-['Outfit'] text-2xl lg:text-3xl font-extralight text-[#0D2D54]">
                      <AnimatedNumber value={s.additionalValue} prefix="£" />
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280] mb-1">Potential Return</p>
                    <p className="font-['Outfit'] text-2xl lg:text-3xl font-extralight text-[#0D2D54]">
                      <AnimatedNumber value={s.returnMultiple} suffix="x" decimals={1} />
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Staging Investment */}
            <div className="bg-[#0D2D54] p-5 text-center mb-10">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">Your Virtual Staging Investment</p>
              <p className="font-['Outfit'] text-2xl font-extralight text-white">
                &pound;{stagingCost.toLocaleString()}
              </p>
            </div>

            {/* CTA */}
            <div className="text-center">
              <h3 className="font-['Outfit'] text-xl font-extralight text-[#0D2D54] mb-6">
                Ready to see your property's potential?
              </h3>
              <Link
                to="/order"
                data-testid="roi-home-cta"
                onClick={handleCTAClick}
                className="inline-flex items-center gap-3 bg-[#0D2D54] text-white px-10 py-4 text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-[#1A3D6B] transition-all"
              >
                Get My Images Staged
                <ArrowRight size={14} />
              </Link>
            </div>

            {/* Disclaimer */}
            <p className="text-[11px] text-[#6B7280]/70 text-center mt-10 max-w-lg mx-auto leading-relaxed">
              These figures are illustrative examples only and do not guarantee future sale prices or returns. Actual results depend on market conditions, pricing, demand, property presentation and other factors.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
